// HorecaHubInsights.jsx
import React, { useState, useMemo } from "react";

const STAKEHOLDERS = [
  {
    id: "owners",
    label: "Owners",
    emoji: "🏨",
    pains: [
      "Low profit margins due to rising input costs.",
      "Fragmented supply chain and price volatility.",
      "Staff shortages and high attrition.",
      "Lack of integrated digital analytics.",
      "Compliance burdens (FSSAI, GST, labour, waste).",
    ],
    opportunities: [
      "Digital twin dashboards for profitability & RCA.",
      "Community procurement & bulk discounts (marketplace).",
      "Staff upskilling + retention programs (skill wallet).",
    ],
  },
  {
    id: "managers",
    label: "Managers",
    emoji: "📋",
    pains: [
      "Scheduling inefficiencies & absenteeism.",
      "Inventory mismanagement & high waste.",
      "Lack of staff performance visibility.",
      "No single CRM/feedback loop.",
      "Pressure handling peak-service operations.",
    ],
    opportunities: [
      "Automated rostering & attendance analytics.",
      "Process scorecards & KPI-triggered alerts.",
      "Integrated customer feedback and SLA tracking.",
    ],
  },
  {
    id: "workers",
    label: "Workers",
    emoji: "🧑‍🍳",
    pains: [
      "Income instability (tips + low wages).",
      "Informal hiring & job insecurity.",
      "Few upskilling opportunities.",
      "Long hours and poor ergonomics.",
      "Limited mental health support.",
    ],
    opportunities: [
      "Microlearning + certified skill wallet.",
      "Gig referrals and marketplace for side incomes.",
      "Mental health & peer-support channels.",
    ],
  },
  {
    id: "chefs",
    label: "Chefs",
    emoji: "👩‍🍳",
    pains: [
      "High stress in peak hours and burnout.",
      "Inconsistent ingredient supply & quality.",
      "Lack of recognition & career paths.",
      "Kitchen safety and hygiene incidents.",
      "Limited channels for showcasing talent.",
    ],
    opportunities: [
      "Recipe library + versioned SOPs & QC checks.",
      "Chef portfolio & consulting gigs marketplace.",
      "Skill certification & public recognition badges.",
    ],
  },
  {
    id: "suppliers",
    label: "Suppliers",
    emoji: "🚚",
    pains: [
      "Delayed payments from buyers.",
      "Unpredictable demand forecasting.",
      "Margin pressure & price competition.",
      "Poor logistics tracking & traceability.",
      "No standardized digital catalogue for buyers.",
    ],
    opportunities: [
      "Demand forecasting & digital order book.",
      "Smart contracts / verified payment pipeline.",
      "Supplier storefronts on marketplace with ratings.",
    ],
  },
  {
    id: "serviceProviders",
    label: "Service Providers",
    emoji: "🛠️",
    pains: [
      "Irregular contracts & low retention.",
      "Hard to prove performance or get reviews.",
      "Payment tracking & invoicing issues.",
      "Difficulty finding local leads quickly.",
      "Opaque pricing and scope definitions.",
    ],
    opportunities: [
      "Verified lead platform + automated invoicing.",
      "Performance dashboard & rating system.",
      "Subscription/service bundles for hotels/restaurants.",
    ],
  },
  {
    id: "customers",
    label: "Customers",
    emoji: "🧑‍🤝‍🧑",
    pains: [
      "Inconsistent food quality or service.",
      "Poor hygiene transparency or trust.",
      "Slow complaint resolution & hidden charges.",
      "Unclear sourcing or sustainability claims.",
      "Limited loyalty consolidation across venues.",
    ],
    opportunities: [
      "Verified Responsible Index (RIM) for venues.",
      "Unified loyalty & refunds workflow.",
      "Transparent ingredient sourcing & ratings.",
    ],
  },
  {
    id: "banks",
    label: "Banks & Financiers",
    emoji: "🏦",
    pains: [
      "Lack of verified digital financial profiles for MSMEs.",
      "High NPAs in hospitality verticals.",
      "Fragmented transaction & POS data.",
      "Difficulty assessing collateral & working capital needs.",
      "Slow loan underwriting for small outlets.",
    ],
    opportunities: [
      "Digital scorecards & transaction-backed lending.",
      "Integrated POS data feeds for credit assessment.",
      "Supply-chain finance via verified invoices.",
    ],
  },
  {
    id: "associationsGov",
    label: "Associations / Government",
    emoji: "🏛️",
    pains: [
      "Poor visibility into informal sector & compliance.",
      "Lack of real-time employment & skills data.",
      "Difficulty enforcing hygiene/waste norms at scale.",
      "Slow adoption of digital certifications.",
      "Fragmented stakeholder coordination channels.",
    ],
    opportunities: [
      "Geo-tagged compliance & skill certification feeds.",
      "Aggregated employment dashboards for policy.",
      "Plug-and-play integrations for government schemes.",
    ],
  },
];

const USPs = [
  {
    id: "digitalTwin",
    title: "Digital Twin & RCA Dashboards",
    desc: "Real-time digital twin for every outlet — operations, finance, compliance, and Root-Cause Analysis (RCA) for issues.",
    emoji: "🛰️",
  },
  {
    id: "skillWellbeing",
    title: "Skill & Wellbeing Stack",
    desc: "Integrated micro-learning, certifications, and mental-health support (skill wallet + peer communities).",
    emoji: "🎓💚",
  },
  {
    id: "rimCertification",
    title: "RIM Responsible Certification",
    desc: "Verified Responsible India Movement (RIM) score — boosts customer trust and regulatory visibility.",
    emoji: "🏅",
  },
  {
    id: "marketplace",
    title: "Verified Marketplace & Supply Chain",
    desc: "Supplier storefronts, demand forecasting, and secure transaction rails for faster procurement & payments.",
    emoji: "🛒",
  },
  {
    id: "communityGigs",
    title: "Community-led Gigs & Revenue Streams",
    desc: "Referral, gigs, rental & micro-entrepreneurship modules to augment worker income and engage communities.",
    emoji: "🤝",
  },
];

/* -------------------- Helper UI subcomponents -------------------- */

const Pill = ({ children }) => (
  <span className="inline-block bg-cyan-700/40 text-cyan-100 px-2 py-0.5 rounded-full text-xs font-medium">
    {children}
  </span>
);

const ExpandIcon = ({ open }) => (
  <span className={`ml-2 text-sm ${open ? "rotate-180" : "rotate-0"} transition-transform`}>
    ▼
  </span>
);

/* -------------------- Main Component -------------------- */

export default function HorecaHubInsights() {
  const [tab, setTab] = useState("stakeholders"); 
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const stakeholdersFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return STAKEHOLDERS;
    return STAKEHOLDERS.filter(
      (s) =>
        s.label.toLowerCase().includes(q) ||
        s.pains.some((p) => p.toLowerCase().includes(q)) ||
        s.opportunities.some((o) => o.toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4 md:p-8 text-gray-100">
      {/* Header */}
      <header className="max-w-6xl mx-auto flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight">Think5Whys — HORECA Insights</h1>
          <p className="text-sm text-gray-300 mt-1">Pain points, opportunities & USPs tailored for Hotels, Restaurants & Cafés.</p>
        </div>

        <div className="flex items-center gap-3">
          <Pill>India: 60K hotels • 5L organised • 2Cr unorganised</Pill>
          <Pill>MMR: 5K hotels • 1.5L outlets</Pill>
        </div>
      </header>

      {/* Tabs */}
      <nav className="max-w-6xl mx-auto mb-4 flex gap-2">
        <button
          onClick={() => setTab("stakeholders")}
          className={`px-3 py-2 rounded-md font-semibold text-sm ${
            tab === "stakeholders" ? "bg-cyan-600 text-black shadow" : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          Stakeholders
        </button>
        <button
          onClick={() => setTab("usps")}
          className={`px-3 py-2 rounded-md font-semibold text-sm ${
            tab === "usps" ? "bg-cyan-600 text-black shadow" : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          Think5Whys USPs
        </button>

        <div className="ml-auto flex items-center gap-2">
          <input
            aria-label="Search stakeholders"
            placeholder="Search stakeholders, pain or opportunity..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="bg-gray-800 text-gray-200 placeholder-gray-400 px-3 py-2 rounded-md text-sm w-56 md:w-80 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <button
            onClick={() => { setQuery(""); setExpandedId(null); }}
            className="text-sm bg-gray-700 px-3 py-2 rounded-md hover:bg-gray-600"
          >
            Reset
          </button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto space-y-6">
        {/* Stakeholders Tab */}
        {tab === "stakeholders" && (
          <>
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stakeholdersFiltered.map((s) => {
                const isOpen = expandedId === s.id;
                return (
                  <article key={s.id} className="bg-gradient-to-br from-gray-800 to-gray-850/30 rounded-lg border border-gray-700 shadow-sm overflow-hidden">
                    <div className="p-4 flex items-start gap-3">
                      <div className="text-3xl">{s.emoji}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <h3 className="text-lg font-semibold">{s.label}</h3>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-300">Stakeholder</span>
                            <button
                              onClick={() => setExpandedId(isOpen ? null : s.id)}
                              aria-expanded={isOpen}
                              className="ml-2 bg-cyan-700/30 text-sm px-2 py-1 rounded"
                            >
                              {isOpen ? "Collapse" : "Expand"} <ExpandIcon open={isOpen} />
                            </button>
                          </div>
                        </div>

                        {/* Snapshot */}
                        <p className="text-sm text-gray-300 mt-2 line-clamp-3">
                          <strong>Pain points:</strong> {s.pains[0]} • {s.pains[1]} • {s.pains[2]}
                        </p>

                        {/* Expanded details */}
                        {isOpen && (
                          <div className="mt-3 space-y-3 text-sm">
                            <div>
                              <h4 className="font-medium">Top 5 Pain Points</h4>
                              <ol className="list-decimal list-inside mt-1 space-y-1 text-gray-200">
                                {s.pains.map((p, idx) => (
                                  <li key={idx}>{p}</li>
                                ))}
                              </ol>
                            </div>

                            <div>
                              <h4 className="font-medium">Opportunities Think5Whys Can Deliver</h4>
                              <ul className="list-disc list-inside mt-1 space-y-1 text-gray-200">
                                {s.opportunities.map((o, idx) => (
                                  <li key={idx}>{o}</li>
                                ))}
                              </ul>
                            </div>

                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => alert(`${s.label} — prototype action: Open Diagnostics`)}
                                className="text-sm bg-cyan-600 text-black px-3 py-1 rounded font-semibold"
                              >
                                Open Diagnostics
                              </button>
                              <button
                                onClick={() => alert(`${s.label} — prototype action: Create Action Plan`)}
                                className="text-sm bg-gray-700 px-3 py-1 rounded hover:bg-gray-600"
                              >
                                Create Action Plan
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </section>

            {stakeholdersFiltered.length === 0 && (
              <div className="bg-gray-800 p-4 rounded text-center text-gray-300">No results for your search.</div>
            )}
          </>
        )}

        {/* USPs Tab */}
        {tab === "usps" && (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {USPs.map((u) => (
              <div key={u.id} className="bg-gradient-to-br from-cyan-700/20 to-gray-800 rounded-lg p-4 border border-cyan-600/20">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{u.emoji}</div>
                  <div>
                    <h3 className="font-semibold text-lg">{u.title}</h3>
                    <p className="text-sm text-gray-200 mt-2">{u.desc}</p>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => alert(`Prototype: Explore ${u.title}`)}
                    className="bg-cyan-600 text-black px-3 py-2 rounded font-semibold text-sm"
                  >
                    Explore
                  </button>
                  <button
                    onClick={() => alert(`Prototype: Demo ${u.title}`)}
                    className="bg-gray-800 px-3 py-2 rounded text-gray-200 hover:bg-gray-700 text-sm"
                  >
                    Demo
                  </button>
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="max-w-6xl mx-auto mt-8 text-center text-sm text-gray-400">
        Think5Whys • HORECA Insights • India & MMR snapshot — built for quick prototyping.
      </footer>
    </div>
  );
}
